// Role picker shared by the three screens that assign or keep Roles.
//
// SHCT-523 — PRD 21 §4.5 [Q81] settles that an admin holds at most ONE Role, so
// 관리자 초대 / 정보 수정 offer a single choice. The first attempt turned the whole
// component into radios and was reverted at review, because the SAME component drives
// the Root-transfer "이양 후 보유할 Role" step, where keeping NO Role is a legitimate
// outcome — and a radio group cannot be cleared once a choice is made.
//
// [ADM.062] (24-08) answered that open question: the transfer step **is** bound by
// 1인 1 Role, and the way to keep both rules is an explicit 「선택 안 함」 radio rather
// than checkboxes. PRD 15 v2.4 §3.5.2 now reads "1개 또는 「선택 안 함」", and PRD 21
// v2.19 §4.3 relaxes `roles` to 「최대 1개」 — zero is valid, two is not.
//
// So: `single` + `allowNone` for the transfer step, `single` alone for the two
// assignment panels. `multi` survives only for callers that have not been reviewed
// against [Q81]; nothing in 어드민 관리 uses it any more.

interface RolePickerProps {
  options: readonly string[];
  value: readonly string[];
  onChange: (roles: string[]) => void;
  /**
   * `single` — exactly one Role, rendered as radios (관리자 초대 / 정보 수정).
   * `multi`  — any number including none, rendered as checkboxes (Root 이양).
   */
  mode?: 'single' | 'multi';
  /**
   * `single` only — adds an explicit 「선택 안 함」 radio so "no Role" stays reachable
   * (PRD 15 v2.4 §3.5.2 · [ADM.062]). Without it a radio group can never be cleared.
   */
  allowNone?: boolean;
  /** Label for the 「선택 안 함」 radio. Required when `allowNone` is set. */
  noneLabel?: string;
  // Disambiguates input IDs when multiple groups live on the same screen
  // (invite + edit panels could in theory coexist).
  idPrefix: string;
  helperText?: string;
  /** Shown when a legacy account already holds more than one Role in `single` mode. */
  multiRoleWarning?: string;
  disabled?: boolean;
}

export function RolePicker({
  options,
  value,
  onChange,
  mode = 'multi',
  allowNone,
  noneLabel,
  idPrefix,
  helperText,
  multiRoleWarning,
  disabled,
  ...rest
}: RolePickerProps & Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'>) {
  const single = mode === 'single';

  // Rows that predate [Q81 폐지] can still carry more than one Role. We do NOT
  // silently drop the extras on mount — `value` is left exactly as loaded, so an
  // edit that never touches this field saves the account unchanged. The warning
  // below says what picking a radio would do.
  const hasLegacyMultiRole = single && value.length > 1;

  const pick = (role: string) => {
    if (single) {
      onChange([role]);
      return;
    }
    onChange(value.includes(role) ? value.filter((r) => r !== role) : [...value, role]);
  };

  return (
    <>
      {/* SHCT-582 — a Role group has no single control to carry the §7.4 P-2 pair, so it sits on
          the group itself: the tooltip covers the whole block, and `closest()` still finds the
          marker from a bypassed radio inside it. */}
      <div
        {...rest}
        style={{ display: 'flex', flexWrap: 'wrap' }}
        role={single ? 'radiogroup' : undefined}
      >
        {single && allowNone ? (
          <label
            htmlFor={`${idPrefix}-none`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 10px',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.6 : 1,
              marginRight: 6,
              marginBottom: 6,
            }}
          >
            <input
              id={`${idPrefix}-none`}
              type="radio"
              name={idPrefix}
              checked={value.length === 0}
              disabled={disabled}
              onChange={() => onChange([])}
            />
            {noneLabel}
          </label>
        ) : null}
        {options.map((role) => {
          const id = `${idPrefix}-${role}`;
          // In single mode only the first stored Role is shown as the current
          // choice — two checked radios in one group is not a state the DOM can
          // represent, and the warning covers the legacy case.
          const checked = single ? value[0] === role : value.includes(role);
          return (
            <label
              key={role}
              htmlFor={id}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 10px',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.6 : 1,
                marginRight: 6,
                marginBottom: 6,
              }}
            >
              <input
                id={id}
                type={single ? 'radio' : 'checkbox'}
                name={single ? idPrefix : undefined}
                checked={checked}
                disabled={disabled}
                onChange={() => pick(role)}
              />
              {role}
            </label>
          );
        })}
      </div>
      {hasLegacyMultiRole && multiRoleWarning && (
        <p
          className="form-field__help"
          style={{ marginTop: 6, fontSize: 11, color: 'var(--warning-foreground, var(--muted-foreground))' }}
        >
          {multiRoleWarning}
        </p>
      )}
      {helperText && (
        <p
          style={{
            marginTop: 6,
            fontSize: 11,
            color: 'var(--muted-foreground)',
          }}
        >
          {helperText}
        </p>
      )}
    </>
  );
}
